import type { LayerRaw } from '@tsyl/karonte/lib/types/layers'

import { LAYER_ATTRIBUTIONS } from '@/constants/layers/attributions'
import type { LayerInfo } from '@/types/LayerInfo'

const info: LayerInfo = {
  description: {
    'en-US': 'Copernicus Global Land Cover Layers: CGLS-LC100 Collection 3',
    'es-ES': 'Copernicus Global Land Cover Layers: CGLS-LC100 Collection 3',
    'pt-BR': 'Copernicus Global Land Cover Layers: CGLS-LC100 Collection 3',
    'fr-FR': 'Copernicus Global Land Cover Layers: CGLS-LC100 Collection 3'
  },
  credit: LAYER_ATTRIBUTIONS.esaCopernicus,
  sourceUrl: 'https://developers.google.com/earth-engine/datasets/catalog/COPERNICUS_Landcover_100m_Proba-V-C3_Global'
}

const vegetationNorthAfrica: LayerRaw = {
  id: 'Vegetation',
  type: 'WMS',
  icon: 'mdi-home',
  url: '/wfas-proxy/geoserver/wms',
  extraArcgisParameter: { copyright: info.credit },
  title: {
    'en-US': 'Vegetation',
    'pt-BR': 'Vegetação',
    'es-ES': 'Vegetación',
    'fr-FR': 'Végétation'
  },
  zindex: 1,
  active: true,
  activeTarget: 'visibility',
  enabled: false,
  customProperties: { group: 'reference', info },
  legend: {
    title: 'n/a'
  },
  sublayers: [
    {
      id: 'naf:NWAF_LC100M',
      visible: true,
      legend: {
        title: {
          'en-US': 'Vegetation',
          'pt-BR': 'Vegetação',
          'es-ES': 'Vegetación',
          'fr-FR': 'Végétation'
        },
        items: [
          {
            text: {
              'en-US': 'Unknown',
              'es-ES': 'Desconocido',
              'pt-BR': 'Desconhecido',
              'fr-FR': 'Inconnu'
            },
            color: '#282828',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Shrubs',
              'es-ES': 'Arbustos',
              'pt-BR': 'Arbustos',
              'fr-FR': 'Arbustes'
            },
            color: '#FFBB22',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Herbaceous Vegetation',
              'es-ES': 'Vegetación Herbácea',
              'pt-BR': 'Vegetação Herbácea',
              'fr-FR': 'Végétation Herbacée'
            },
            color: '#FFFF4C',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Cultivated and managed vegetation or agriculture',
              'es-ES': 'Vegetación cultivada y gestionada o agricultura',
              'pt-BR': 'Vegetação cultivada e gerenciada ou agricultura',
              'fr-FR': 'Végétation cultivée et gérée ou agriculture'
            },
            color: '#F096FF',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Urban',
              'es-ES': 'Urbano',
              'pt-BR': 'Urbano',
              'fr-FR': 'Urbain'
            },
            color: '#FA0000',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Bare or sparse vegetation',
              'es-ES': 'Vegetación escasa o desnuda',
              'pt-BR': 'Vegetação escassa ou nua',
              'fr-FR': 'Végétation clairsemée ou nue'
            },
            color: '#B4B4B4',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Snow and ice',
              'es-ES': 'Nieve y hielo',
              'pt-BR': 'Neve e gelo',
              'fr-FR': 'Neige et glace'
            },
            color: '#F0F0F0',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Permanent water bodies',
              'es-ES': 'Cuerpos de agua permanentes',
              'pt-BR': "Corpos d'água permanentes",
              'fr-FR': "Plans d'eau permanents"
            },
            color: '#0032C8',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Herbaceous wetland',
              'es-ES': 'Humedal herbáceo',
              'pt-BR': 'Zona úmida herbácea',
              'fr-FR': 'Zone humide herbacée'
            },
            color: '#0096A0',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Moss and lichen',
              'es-ES': 'Musgo y liquen',
              'pt-BR': 'Musgo e líquen',
              'fr-FR': 'Mousse et lichen'
            },
            color: '#FAE6A0',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Closed forest, evergreen needle leaf',
              'es-ES': 'Bosque cerrado, hoja de aguja perenne',
              'pt-BR': 'Floresta fechada, folha de agulha perene',
              'fr-FR': 'Forêt fermée, feuillus persistants à aiguilles'
            },
            color: '#58481F',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Closed forest, evergreen broad leaf',
              'es-ES': 'Bosque cerrado, hoja ancha perenne',
              'pt-BR': 'Floresta fechada, folha larga perene',
              'fr-FR': 'Forêt fermée, feuillus persistants à feuilles larges'
            },
            color: '#009900',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Closed forest, deciduous needle leaf',
              'es-ES': 'Bosque cerrado, hoja de aguja caducifolia',
              'pt-BR': 'Floresta fechada, folha de agulha decídua',
              'fr-FR': 'Forêt fermée, feuillus caducs à aiguilles'
            },
            color: '#70663E',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Closed forest, deciduous broad leaf',
              'es-ES': 'Bosque cerrado, hoja ancha caducifolia',
              'pt-BR': 'Floresta fechada, folha larga decídua',
              'fr-FR': 'Forêt fermée, feuillus caducs à feuilles larges'
            },
            color: '#00CC00',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Closed forest, mixed',
              'es-ES': 'Bosque cerrado, mixto',
              'pt-BR': 'Floresta fechada, mista',
              'fr-FR': 'Forêt fermée, mixte'
            },
            color: '#4E751F',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Closed forest, not matching any of the other definitions',
              'es-ES': 'Bosque cerrado, no coincide con ninguna otra definición',
              'pt-BR': 'Floresta fechada, não corresponde a nenhuma outra definição',
              'fr-FR': 'Forêt fermée, ne correspondant à aucune autre définition'
            },
            color: '#007800',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Open forest, evergreen needle leaf',
              'es-ES': 'Bosque abierto, hoja de aguja perenne',
              'pt-BR': 'Floresta aberta, folha de agulha perene',
              'fr-FR': 'Forêt ouverte, feuillus persistants à aiguilles'
            },
            color: '#666000',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Open forest, evergreen broad leaf',
              'es-ES': 'Bosque abierto, hoja ancha perenne',
              'pt-BR': 'Floresta aberta, folha larga perene',
              'fr-FR': 'Forêt ouverte, feuillus persistants à feuilles larges'
            },
            color: '#8DB400',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Open forest, deciduous needle leaf',
              'es-ES': 'Bosque abierto, hoja de aguja caducifolia',
              'pt-BR': 'Floresta aberta, folha de agulha decídua',
              'fr-FR': 'Forêt ouverte, feuillus caducs à aiguilles'
            },
            color: '#8D7400',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Open forest, mixed',
              'es-ES': 'Bosque abierto, mixto',
              'pt-BR': 'Floresta aberta, mista',
              'fr-FR': 'Forêt ouverte, mixte'
            },
            color: '#929900',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Open forest, not matching any of the other definitions',
              'es-ES': 'Bosque abierto, no coincide con ninguna otra definición',
              'pt-BR': 'Floresta aberta, não corresponde a nenhuma outra definição',
              'fr-FR': 'Forêt ouverte, ne correspondant à aucune autre définition'
            },
            color: '#648C00',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Oceans, seas',
              'es-ES': 'Océanos, mares',
              'pt-BR': 'Oceanos, mares',
              'fr-FR': 'Océans, mers'
            },
            color: '#000080',
            icon: 'mdi-square'
          }
        ]
      }
    }
  ]
}

export default vegetationNorthAfrica
